// ============================================================
// ANSICHTSSACHE-AGENT – Die Spalte, die nachgibt
// ============================================================
// In einer Listenzeile gibt es zwei Arten von Spalten: feste
// (Nummer, Datum, Plakette, Betrag — sie stehen auf
// `whitespace-nowrap` und sind so breit, wie sie sein muessen)
// und nachgebende (Titel, Kunde, Beschreibung). Dieses Bauteil
// ist die nachgebende: EINE Zeile, der Rest als
// Auslassungspunkte.
//
// WARUM KEIN UMBRUCH: eine zweizeilige Zelle macht die ganze
// Zeile zweizeilig, und damit sind die Zeilen einer Liste
// ungleich hoch. Eine Liste liest man senkrecht — Nummer unter
// Nummer, Betrag unter Betrag; das geht nur bei gleichem
// Zeilenmass. Der volle Text steht auf dem Blatt, das die Zeile
// oeffnet, und ist ueber die Suche im Kopf auffindbar.
//
// WARUM `w-full max-w-0` UND NICHT NUR `truncate`: `Table`
// rechnet mit automatischer Breite, und dort bemisst der Browser
// jede Spalte am laengsten Inhalt darin. Eine Zelle, die nicht
// umbricht, macht die Spalte also so breit wie ihr laengster
// Titel und schiebt die Tabelle aus der Karte. Es braucht beides
// zusammen:
//
//   max-w-0   nimmt der Spalte den Anspruch auf die Breite ihres
//             laengsten Inhalts
//   w-full    gibt ihr dafuer den Platz, den die festen Spalten
//             uebrig lassen — und nur den
//
// Am 20.09.2026 in der Rechnungsliste nachgemessen (1440er Bild,
// Karte 1132 px): nur `max-w-0` ergibt eine Titelspalte von
// 43 px, weil der freie Platz zu den Datumsspalten wandert; nur
// `w-full` ergibt 723 px und eine Tabelle mit Rollbalken; beides
// zusammen 311 px ohne Rollbalken.
//
// `breite` TEILT DEN FREIEN PLATZ, wenn eine Liste ZWEI
// nachgebende Spalten hat (Titel und Kunde). Ohne Angabe fordert
// jede den ganzen Rest an, und der Browser teilt ihn nicht
// haelftig, sondern deutlich zugunsten der ersten Spalte — in der
// Rechnungsliste 209 zu 82 Pixeln, der Kundenname war nach drei
// Buchstaben zu Ende. Mit 60/40 sind es 157 zu 134.
//
// KEIN `Tooltip` mit dem vollen Text: sein Wrapper kann in dieser
// Zelle nicht schrumpfen (die Kuerzung greift dann nicht) und
// liegt unter der Zeilenflaeche, faengt also keinen Zeiger.
//
// AUF DEM TELEFON HAT DIE SPALTE EINE UNTERGRENZE (`--rastertext`,
// seit 10.10.2026). Neben Nummer, Datum, Zustand und Betrag blieben
// dem Titel auf dem iPhone 40 Pixel — „Rechn…“. Der Inhalt traegt
// deshalb dort eine FESTE Breite, und die Zelle gibt `max-w-0`
// und `breite` auf — gegen `max-w-0` setzt sich eine Mindestbreite
// des Inhalts in einer Tabellenzelle nicht durch, der Text lief
// nur ueber die Nachbarspalte. Die Tabelle wird breiter als das
// Bild und rollt in ihrer Karte seitlich (`Table` hat dafuer
// `overflow-x-auto`). Auf breiten Schirmen bleibt alles, wie oben
// beschrieben.
// ============================================================

import { TableCell } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export function Textzelle({
  text,
  zusatz,
  breite,
  className,
}: {
  text: React.ReactNode;
  /**
   * Was am Text klebt und nicht gekuerzt werden darf — die
   * Bueroklammer zum Beleg etwa. Gekuerzt wird immer der Text.
   */
  zusatz?: React.ReactNode;
  /**
   * Anteil am freien Platz in Prozent. Nur noetig, wo zwei
   * nachgebende Spalten nebeneinander stehen; dann tragen beide
   * einen Anteil und die Summe ist 100.
   */
  breite?: number;
  className?: string;
}) {
  return (
    <TableCell
      // `data-textzelle`: die erste davon ist der Titel der Zeile und
      // bleibt auf dem Telefon beim seitlichen Wischen stehen
      // (`styles/globals.css`, Listentabelle).
      data-textzelle=""
      className={cn("w-full max-w-0 max-md:w-auto! max-md:max-w-none", className)}
      style={breite === undefined ? undefined : { width: `${breite}%` }}
    >
      <span className="flex min-w-0 items-center gap-1.5 max-md:w-[var(--rastertext)]">
        <span className="truncate">{text}</span>
        {zusatz ? <span className="flex shrink-0 items-center gap-1.5">{zusatz}</span> : null}
      </span>
    </TableCell>
  );
}
